import { useEffect, useState } from 'react';
import { Dialog, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { registrarControlLiquidar } from '../../services/tramiteService';
import { apellidoNombre } from '../../utils/nombrePersona';
import { useSincronizarFecha } from '../Tramite/CampoFechaLiquidar';

const COLOR = '#1e40af';

const formatoFecha = (valor) => {
    if (!valor) return null;
    const fecha = new Date(`${valor}T00:00:00`);
    return Number.isNaN(fecha.getTime()) ? null : fecha.toLocaleDateString('es-AR');
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

/** Mientras se tipea, el año pasa por "0002": no se habilita Guardar hasta que sea un año real. */
const anioValido = (valor) => {
    const anio = Number(String(valor || '').slice(0, 4));
    return Number.isInteger(anio) && anio >= 1900 && anio <= 2100;
};

/**
 * Gestión de una carpeta del listado "A liquidar": controlarla y/o correrle la fecha de inicio.
 *
 * POR QUÉ LAS DOS COSAS EN UN SOLO MODAL
 * En el escritorio del estudio son un solo gesto. La carpeta se saca del archivo un mes antes de
 * la fecha de liquidar, se revisa que esté todo (aportes, plan de pago, firmas) y ahí mismo se
 * decide si la fecha sigue en pie o hay que correrla. Dos botones separados obligarían a escribir
 * la nota dos veces y dejarían dos líneas sueltas en el historial para algo que pasó una vez.
 *
 * LA OBSERVACIÓN ES OBLIGATORIA
 * Es lo único que queda en el historial de estados del trámite. Sin ella, dentro de tres meses
 * nadie sabe por qué una carpeta que vencía en junio ahora vence en septiembre — que es
 * exactamente el problema que este modal viene a resolver.
 */
export default function ModalGestionarCarpeta({ open, fila, onClose, onGuardado }) {
    const [marcarControlada, setMarcarControlada] = useState(true);
    const [fechaControl, setFechaControl] = useState(hoyISO());
    const [nota, setNota] = useState('');
    const [cambiarFecha, setCambiarFecha] = useState(false);
    const [nuevaFechaInicio, setNuevaFechaInicio] = useState('');
    const [iniciaAhora, setIniciaAhora] = useState(false);
    const [guardando, setGuardando] = useState(false);
    // Los dos campos de fecha son no controlados: con value={...} tipear el año se cortaba en el
    // primer dígito (ver useSincronizarFecha).
    const refFechaControl = useSincronizarFecha(fechaControl);
    const refNuevaFecha = useSincronizarFecha(nuevaFechaInicio);

    // Depende solo de `open` y del id, no del objeto `fila`: TablaListado rearma las filas en
    // cada recarga y un `fila` nuevo por render reabriría el formulario en blanco mientras
    // alguien está escribiendo la nota.
    const idTramite = fila?.idTramite;
    useEffect(() => {
        if (!open) return;
        setMarcarControlada(true);
        setFechaControl(hoyISO());
        setNota('');
        setCambiarFecha(false);
        setNuevaFechaInicio('');
        setIniciaAhora(false);
    }, [open, idTramite]);

    if (!fila) return null;

    const notaLimpia = nota.trim();
    const puedeGuardar = notaLimpia !== ''
        && !guardando
        && (!cambiarFecha || iniciaAhora || anioValido(nuevaFechaInicio));

    const guardar = async () => {
        setGuardando(true);
        try {
            await registrarControlLiquidar(fila.idTramite, {
                marcarControlada,
                fechaControl: marcarControlada ? fechaControl : null,
                nota: notaLimpia,
                // Excluyentes: "inicia ahora" pone la fecha de hoy, la otra la corre a la elegida.
                iniciaAhora: cambiarFecha && iniciaAhora,
                nuevaFechaInicio: cambiarFecha && !iniciaAhora ? nuevaFechaInicio : null,
            });
            toast.success('Carpeta actualizada. Quedó registrado en el historial del trámite.');
            onGuardado?.();
            onClose?.();
        } catch (err) {
            toast.error(err.response?.data || 'No se pudo guardar la gestión de la carpeta.');
        } finally {
            setGuardando(false);
        }
    };

    const fechaActual = formatoFecha(fila.fechaInicioEstimada);
    const proxima = (fila.proximasFechas || [])[0] || null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflowY: 'auto' } }}>
            <Box sx={{ background: `${COLOR}12`, borderBottom: `1px solid ${COLOR}33`, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="fw-bold" style={{ color: '#1e293b', fontSize: '1.05rem' }}>
                    <i className="bi bi-clipboard-check me-2" style={{ color: COLOR }}></i>Gestionar carpeta
                </span>
                <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
                <div className="mb-3 pb-3 border-bottom">
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>
                        {apellidoNombre(fila.apellidoCliente, fila.nombreCliente) || 'Sin nombre'}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        {fila.tipoTramite || 'Trámite'}
                        {fila.carpetaNombre && ` · Carpeta ${fila.carpetaNombre}`}
                        {' · '}
                        {fechaActual
                            ? <span style={{ color: fila.vencido ? '#b91c1c' : 'inherit', fontWeight: fila.vencido ? 600 : 400 }}>
                                LIQ {fechaActual}{fila.vencido && ' (vencida)'}
                            </span>
                            : 'sin fecha asignada'}
                    </div>
                    {fila.fechaControlLiquidar && (
                        <div style={{ fontSize: '0.8rem', color: '#047857', marginTop: 4 }}>
                            <i className="bi bi-check-circle-fill me-1" />
                            Último control: {formatoFecha(fila.fechaControlLiquidar)}
                            {fila.usuarioControlLiquidar && ` — ${fila.usuarioControlLiquidar}`}
                            {fila.notaControlLiquidar && <div style={{ color: '#6b7280' }}>{fila.notaControlLiquidar}</div>}
                        </div>
                    )}
                </div>

                <div className="form-check mb-3">
                    <input
                        className="form-check-input" type="checkbox" id="marcarControlada"
                        checked={marcarControlada}
                        onChange={(e) => setMarcarControlada(e.target.checked)}
                    />
                    <label className="form-check-label fw-semibold" htmlFor="marcarControlada">
                        Marcar la carpeta como controlada
                    </label>
                    {marcarControlada && (
                        <div className="mt-2" style={{ maxWidth: 200 }}>
                            <input
                                type="date" className="form-control form-control-sm"
                                ref={refFechaControl} defaultValue={fechaControl}
                                onChange={(e) => setFechaControl(e.target.value)}
                            />
                        </div>
                    )}
                </div>

                <div className="form-check mb-3">
                    <input
                        className="form-check-input" type="checkbox" id="cambiarFecha"
                        checked={cambiarFecha}
                        onChange={(e) => setCambiarFecha(e.target.checked)}
                    />
                    <label className="form-check-label fw-semibold" htmlFor="cambiarFecha">
                        Cambiar la fecha de inicio
                    </label>
                    {/* La próxima cosa que anotó la planilla ("Después: Transf. a JUB 07/2032"): con
                        un clic pasa a ser la fecha de liquidar y deja de figurar como próxima. */}
                    {proxima && (
                        <div className="mt-2">
                            <button
                                type="button" className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                    setCambiarFecha(true);
                                    setIniciaAhora(false);
                                    setNuevaFechaInicio(proxima.fecha);
                                }}
                            >
                                <i className="bi bi-skip-forward me-1" />
                                Pasar a la próxima fecha: {formatoFecha(proxima.fecha)}
                            </button>
                            {proxima.descripcion && (
                                <div style={{ fontSize: '0.8rem', color: '#4b5563', marginTop: 4 }}>{proxima.descripcion}</div>
                            )}
                        </div>
                    )}
                    {cambiarFecha && (
                        <div className="mt-2">
                            <div className="form-check">
                                <input
                                    className="form-check-input" type="checkbox" id="iniciaAhora"
                                    checked={iniciaAhora}
                                    onChange={(e) => setIniciaAhora(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="iniciaAhora" style={{ fontSize: '0.9rem' }}>
                                    Ya se puede iniciar (toma la fecha de hoy y pasa al mes corriente)
                                </label>
                            </div>
                            {!iniciaAhora && (
                                <div style={{ maxWidth: 200 }} className="mt-2">
                                    <input
                                        type="date" className="form-control form-control-sm"
                                        ref={refNuevaFecha} defaultValue={nuevaFechaInicio}
                                        min="1900-01-01" max="2100-12-31"
                                        onChange={(e) => setNuevaFechaInicio(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="mb-1">
                    <label className="form-label fw-semibold" htmlFor="notaControl">
                        Observación <span style={{ color: '#b91c1c' }}>*</span>
                    </label>
                    <textarea
                        id="notaControl" className="form-control" rows={3} value={nota}
                        onChange={(e) => setNota(e.target.value)}
                        placeholder="Le quedan por comprar 41 meses con moratoria 27.705, llamé para comentarle y no responde."
                    />
                    <div className="form-text">
                        Queda en el historial de estados del trámite, junto con el cambio de fecha si lo hay.
                    </div>
                </div>
            </Box>

            <Box sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <button type="button" className="btn btn-light" onClick={onClose} disabled={guardando}>Cancelar</button>
                <button type="button" className="btn btn-primary" onClick={guardar} disabled={!puedeGuardar}>
                    {guardando ? 'Guardando…' : 'Guardar'}
                </button>
            </Box>
        </Dialog>
    );
}
